// src/helpers/apiCache.ts
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const apiCache = new Map<string, CacheEntry<any>>();

/**
 * Default cache TTL: 5 minutes
 */
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

// Counter to trigger cleanup occasionally rather than on every single call
let accessCount = 0;

/**
 * Lazily cleans up expired cache entries.
 * Runs only when cache is accessed to prevent background memory leak/CPU drain.
 */
function lazyCleanup() {
  const now = Date.now();
  let cleanedCount = 0;
  
  for (const [key, entry] of apiCache.entries()) {
    if (now - entry.timestamp > DEFAULT_CACHE_TTL_MS) {
      apiCache.delete(key);
      cleanedCount++;
    }
  }
  
  if (cleanedCount > 0 && process.env.NODE_ENV !== "production") {
    console.debug(`[Cache Lazy-Cleanup] Removed ${cleanedCount} expired entries. Size: ${apiCache.size}`);
  }
}

/**
 * Get data from cache or fetch it using the provided function
 */
export async function getCachedData<T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttlMs: number = DEFAULT_CACHE_TTL_MS
): Promise<T> {
  // Trigger cleanup every ~50 cache accesses
  if (++accessCount % 50 === 0) lazyCleanup();

  const cached = apiCache.get(key) as CacheEntry<T> | undefined;
  const now = Date.now();

  // Return cached data if still valid
  if (cached && now - cached.timestamp < ttlMs) {
    return cached.data;
  }

  // Fetch fresh data
  const data = await fetchFn();

  // Store in cache
  apiCache.set(key, { data, timestamp: now });

  return data;
}

/**
 * Invalidate a cache entry to force refresh on next call
 */
export function invalidateCache(key: string): void {
  apiCache.delete(key);
}

/**
 * Invalidate all cache entries matching a pattern
 */
export function invalidateCachePattern(pattern: string): void {
  const keysToDelete: string[] = [];
  for (const key of apiCache.keys()) {
    if (key.includes(pattern)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => apiCache.delete(key));
}

/**
 * Clear all cached data
 */
export function clearAllCache(): void {
  apiCache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;

  for (const entry of apiCache.values()) {
    if (now - entry.timestamp < DEFAULT_CACHE_TTL_MS) validEntries++;
    else expiredEntries++;
  }

  const estimatedMemoryMB = (JSON.stringify(Array.from(apiCache.values())).length / 1024 / 1024).toFixed(2);

  return { total: apiCache.size, valid: validEntries, expired: expiredEntries, estimatedMemoryMB };
}